import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES } from '@/constants/apiRoutes'
import type {
  CardSetManagement,
  CardSetManagementState,
  CardSetManagementListResponse,
  CreateCardSetParam,
  EditCardSetParam,
} from '@/types'

export const getCardSets = createAsyncThunk<CardSetManagement[]>(
  API_ROUTES.ADMIN_CARDSET_ENDPOINT,
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<CardSetManagementListResponse>(
        API_ROUTES.ADMIN_CARDSET_ENDPOINT
      )
      const data = (res as any).data ? (res as any).data : res
      return data.items ?? []
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail || error?.message || '取得に失敗しました'
      )
    }
  }
)

export const createCardSet = createAsyncThunk<CardSetManagement, CreateCardSetParam>(
  'cardsetManagement/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<CardSetManagement>(
        API_ROUTES.ADMIN_CARDSET_ENDPOINT,
        payload
      )
      const data = (res as any).data ? (res as any).data : res
      return data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail || error?.message || '登録に失敗しました'
      )
    }
  }
)

export const editCardSet = createAsyncThunk<CardSetManagement, EditCardSetParam>(
  'cardsetManagement/edit',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put<CardSetManagement>(
        `${API_ROUTES.ADMIN_CARDSET_ENDPOINT}/${id}`,
        payload
      )
      const data = (res as any).data ? (res as any).data : res
      return data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail || error?.message || '更新に失敗しました'
      )
    }
  }
)

export const deleteCardSet = createAsyncThunk<number, number>(
  'cardsetManagement/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${API_ROUTES.ADMIN_CARDSET_ENDPOINT}/${id}`)
      return id
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail || error?.message || '削除に失敗しました'
      )
    }
  }
)

const initialState: CardSetManagementState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
}

const cardsetManagementSlice = createSlice({
  name: 'cardsetManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCardSets.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCardSets.fulfilled, (state, action) => {
        state.items = action.payload
        state.loading = false
      })
      .addCase(getCardSets.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Error'
        state.loading = false
      })

      .addCase(createCardSet.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(createCardSet.fulfilled, (state) => {
        state.saving = false
      })
      .addCase(createCardSet.rejected, (state, action) => {
        state.saving = false
        state.error = (action.payload as string) || action.error.message || 'Error'
      })

      .addCase(editCardSet.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(editCardSet.fulfilled, (state) => {
        state.saving = false
      })
      .addCase(editCardSet.rejected, (state, action) => {
        state.saving = false
        state.error = (action.payload as string) || action.error.message || 'Error'
      })

      .addCase(deleteCardSet.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(deleteCardSet.fulfilled, (state, action) => {
        state.saving = false
        state.items = state.items.filter((item) => item.id !== action.payload)
      })
      .addCase(deleteCardSet.rejected, (state, action) => {
        state.saving = false
        state.error = (action.payload as string) || action.error.message || 'Error'
      })
  },
})

export default cardsetManagementSlice.reducer